import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { readApiError, type CreditLedger } from "@/lib/cohort-credits";
import { L, SummaryGrid, HistoryTable } from "@/components/credit-ledger-view";

const BASE = import.meta.env.BASE_URL;

export default function InvestorCreditSummary({ cohortId }: { cohortId: number | null }) {
  const [data, setData] = useState<CreditLedger | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const refresh = () => setNonce(n => n + 1);
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(() => { if (!document.hidden) refresh(); }, 60000);
    return () => { window.removeEventListener("focus", refresh); window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (!cohortId) { setData(null); return; }
    let live = true;
    setLoading(true); setError("");
    fetch(`${BASE}api/investor-portal/cohort-credits?cohortId=${cohortId}`, { headers: { "x-investor-token": localStorage.getItem("investor_token") || "" } })
      .then(async r => { if (!r.ok) throw new Error(await readApiError(r)); return r.json(); })
      .then(b => { if (live) setData(b); })
      .catch(e => { if (live) setError(e.message || "Could not load your credits."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [cohortId, nonce]);

  if (!cohortId) return null;
  return (
    <section className={`${L.panel} border ${L.border} rounded-2xl p-5 space-y-4`} data-testid="section-investor-credits">
      <div>
        <h2 className="text-base font-serif font-bold flex items-center gap-2"><Wallet className="w-4 h-4 text-[#C64600] dark:text-[#FF7A2E]" /> Research + Allocation Credit</h2>
        <p className={`text-xs mt-1 ${L.muted}`}>For the selected cohort. Read-only; contact Favcy if anything looks wrong. Earlier investments are shown elsewhere and are not deducted here.</p>
      </div>
      {loading ? <div className={`h-28 rounded-xl animate-pulse ${L.sub}`} />
        : error ? <div className={`rounded-lg border px-3 py-2 text-sm ${L.error}`}>{error} <button className="underline font-semibold ml-1" onClick={() => setNonce(n => n + 1)} data-testid="button-credits-retry">Retry</button></div>
        : !data?.summary || !data.entries.length ? <p className={`text-sm ${L.muted}`}>No payments or credits recorded for this cohort yet. This does not mean an earlier payment was not received; contact Favcy if your records have not yet been added.</p>
        : <><SummaryGrid s={data.summary} /><HistoryTable entries={data.entries} companies={data.companies} /></>}
    </section>
  );
}
